import { Product } from "../types/product";
import { Country, HkRegion } from "../types/country";
import {
  PickupAddr,
  SfDistrict,
  SfLocation,
  SfShippingMethod,
  ShipAddr,
  ShipSetting,
  ShipSettingOption,
  ShipName,
} from "../types/shipAddr";
import { get, post, getDontHandleError } from "./fetch.core";
import { useTranslation } from "./translation.core";
import messages from "../i18n/messages";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";

// First, let's define the correct Country interface extension
interface CountryWithDefault extends Country {
  isDefault?: boolean;
  iso: string;
}

async function getShippingCountries() {
  const res = await get<Country[]>("/public/shop/shippingCountries");
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error("Failed to fetch shipping countries");
}

async function getHkRegions() {
  const res = await get<HkRegion[]>("/public/shop/regionsHK");
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error("Failed to fetch HK regions");
}

async function addAddr(address: ShipAddr) {
  const res = await post<ShipAddr>("/public/user/addShipAddr", address);
  return res.data;
}

async function updateAddr(address: ShipAddr) {
  const res = await post<ShipAddr>(`/public/user/updateShipADdr?id=${address.id}`, address);
  return res.data;
}

async function getAddrs() {
  const res = await get<ShipAddr[]>("/public/user/shipAddrs");
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error("Failed to fetch shipping addresses");
}

async function deleteAddrs(addressId: string) {
  try {
    const res = await post<{ message: string }>(`/public/user/deleteShipAddr?id=${addressId}`, {});
    return res.data;
  } catch (error) {
    console.warn("Error deleting shipping address:", error);
    // Return success message as fallback
    return { message: "Address deleted successfully" };
  }
}

async function getSfSubDistricts() {
  const res = await get<SfDistrict[]>("/public/ship/sfSubDistricts");
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error("Failed to fetch SF sub-districts");
}

async function getSfLocations(subDistrict: string) {
  // `subDistrict` is the zh-Hant sub-district key returned by getSfSubDistricts
  const res = await get<SfLocation[]>(`/public/ship/sfLocations?subDistrict=${encodeURIComponent(subDistrict)}`);
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error(`Failed to fetch SF locations for ${subDistrict}`);
}

async function getSfLocationByCode(locationCode: string) {
  const res = await get<SfLocation>(`/public/ship/sfLocation?code=${locationCode}`);
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error(`Failed to fetch SF location for code ${locationCode}`);
}

async function getPickupAddrs(locale: string, countryId?: string) {
  const qs = countryId ? `?countryId=${encodeURIComponent(countryId)}` : "";
  const res = await get<PickupAddr[]>(`/public/ship/pickupAddrs${qs}`, { locale });
  return res.data;
}

export function useShippingMethodLocale() {
  const { t } = useTranslation();

  function getShippingMethod(shippingMethod: SfShippingMethod) {
    switch (shippingMethod) {
      case "business_station":
        return t(messages.shipping.businessStation);
      case "locker":
        return t(messages.shipping.locker);
      case "service_partner":
        return t(messages.shipping.servicePartner);
      case "store":
        return t(messages.shipping.store);
      default:
        return shippingMethod;
    }
  }

  return {
    getShippingMethod,
  };
}

async function getShipSettings(countryId: string) {
  const res = await get<ShipSetting[]>(`/public/ship/settings?countryId=${countryId}`);
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error(`Failed to fetch ship settings for country ${countryId}`);
}

async function getShipSettingsByCode(countryCode: string) {
  const res = await get<ShipSetting[]>(`/public/ship/settings?countryCode=${countryCode}`);
  if (res.status === 200 && res.data) {
    return res.data;
  }
  throw new Error(`Failed to fetch ship settings for country ${countryCode}`);
}

interface BrandOption {
  name: string;
  count: number;
  code: string;
}
interface AttributeOption {
  value: string;
  count: number;
}

interface ProductListResponse {
  list: Product[];
  size: number;
  attributeOptions: AttributeOption[];
  brandOptions: BrandOption[];
}

export const getProducts = async (params: {
  page?: number;
  size?: number;
  getAttributeOptions?: boolean;
  getBrandOptions?: boolean;
  attributes?: string[];
  brands?: string[];
}): Promise<ProductListResponse> => {
  const body: Record<string, any> = {};

  if (params.page !== undefined) body.page = params.page;
  if (params.size !== undefined) body.size = params.size;
  if (params.getAttributeOptions) body.getAttributeOptions = true;
  if (params.getBrandOptions) body.getBrandOptions = true;
  if (params.attributes && params.attributes.length > 0) body.attributes = params.attributes;
  if (params.brands && params.brands.length > 0) body.brands = params.brands;

  const res = await post<ProductListResponse>(`/public/product/list`, body);
  return res.data;
};

const shipCore = {
  getShippingCountries,
  getHkRegions,
  addAddr,
  updateAddr,
  getAddrs,
  deleteAddrs,
  getSfSubDistricts,
  getSfLocations,
  getSfLocationByCode,
  getPickupAddrs,
  getShipSettings,
  getShipSettingsByCode,
};

export default shipCore;
